import { Shipment } from "@/types/inventory/shipment";

import { roundQuantity } from "./shipment-picker";

/** The lot fields a checkout line carries. Both checkout trees pass a ChargeItem. */
export interface ChargeLineLots {
  shipmentIds?: string[];
  shipmentAllocations?: {
    shipmentId: string;
    quantity: number;
    allowExtraYield?: boolean;
  }[];
}

export interface ResolvedLot {
  shipmentId: string;
  lotNumber: string | undefined;
  quantity: number;
  sku?: string;
  allowExtraYield?: boolean;
}

/**
 * Read one line's lot split, scoped to a single stock's shipments.
 *
 * Lives here rather than in either checkout tree: v2 and the legacy v1 checkout
 * each render their own picker, and this is the part that must not drift.
 *
 * Older charges (and anything saved before per-lot quantities existed) only
 * carry ids, so mirror what the server would spend: lots take what they can in
 * the order they were picked.
 */
export function resolveChargeLineLots(
  line: ChargeLineLots | undefined,
  shipments: Shipment[],
  quantityNeeded: number,
): ResolvedLot[] {
  const shipmentById = new Map(shipments.map((s) => [s.id, s]));

  if (line?.shipmentAllocations?.length) {
    return line.shipmentAllocations
      .filter((allocation) => shipmentById.has(allocation.shipmentId))
      .map((allocation) => {
        const shipment = shipmentById.get(allocation.shipmentId);
        return {
          shipmentId: allocation.shipmentId,
          lotNumber: shipment?.lotNumber,
          quantity: allocation.quantity,
          ...(allocation.allowExtraYield && { allowExtraYield: true }),
          sku: shipment?.sku,
        };
      });
  }

  let remaining = quantityNeeded;
  return (line?.shipmentIds ?? [])
    .filter((shipmentId) => shipmentById.has(shipmentId))
    .map((shipmentId) => {
      const shipment = shipmentById.get(shipmentId);
      const quantity = roundQuantity(
        Math.min(shipment?.remainingQuantity ?? 0, remaining),
      );
      remaining = Math.max(roundQuantity(remaining - quantity), 0);
      return {
        shipmentId,
        lotNumber: shipment?.lotNumber,
        quantity,
        sku: shipment?.sku,
      };
    });
}
